import { lookOf, type SettingLookCode } from '@gk/contracts'

/** M25-P (docs/M25.md A and E, ADR-129): THE GEOMETRY OF THE SETTINGS' PICTURES — pure (no React, no I/O, no clock), tested in lookGeometry.test.ts.
 *  `ui/looks/` turns what it returns into the app's own inline SVG (stroke and fill in currentColor only, M17).
 *
 *  The frame is the area's own clock, as the library says (`packages/contracts/src/settingLooks.ts`): 12 is the back of the green (the far end of a
 *  fairway), 6 is where the play arrives — the bottom of every picture. A heading h, in degrees clockwise from 12, is the SVG vector (sin h, −cos h)
 *  (SVG's y grows downward), so 1:30 (45°) rises to the right seen from 6 and a clockwise lap turns clockwise on the screen, as on the map, seen from
 *  above. Only a code's MEANING is read (`lookOf`), never its spelling and never a word of a club (rule 4); the generic shape a picture is drawn on
 *  comes from the area type codes and decides nothing (rule 5).
 *
 *  What each family draws (the plan's table A):
 *  - dir: seven bands across the shape, alternate bands at 0.16, the outline; an axis has a two-headed line on the middle band, a first pass one
 *    chevron there pointing to its heading;
 *  - pat: stripes at their angle (along the line of play with arrows toward 12, or toward 6 for the reverse cut); a checkerboard or a diamond as two
 *    band sets at 0.12; 50/50 as the shape split along the line of play with opposed arrows; up-and-back as one long path in alternate senses;
 *    around as concentric loops;
 *  - lap: the outline scaled to 0.88 (the shape faintly filled under it), two chevrons tangent at 2 and 8 o'clock for a sense, none for a lap whose
 *    sense is not said, dashed at 0.4 with a slash for no lap;
 *  - tool: a side view — the ground line, the roller, a bladed reel above the ground with an up chevron (raised) or on it (lowered). */

export type LookFrame = 'round' | 'block' | 'strip'
export const LOOK_FRAMES: readonly LookFrame[] = ['round', 'block', 'strip']
/** 24: the pictogram (cards, chips, strips); 48: the schema (tiles, the shed screen, an outline) */
export type LookGrid = 24 | 48
export interface Pt { readonly x: number; readonly y: number }
export type Ring = readonly Pt[]
/** A surface to draw on, in the box's units: its outer rings (one per part), its holes, its centre (the centroid for a real outline), its box. */
export interface LookShape { readonly outers: readonly Ring[]; readonly holes: readonly Ring[]; readonly c: Pt; readonly box: number }

const rad = (d: number) => (d * Math.PI) / 180
const deg = (r: number) => (r * 180) / Math.PI
const tidy = (n: number) => (Math.abs(n) < 1e-12 ? 0 : n)
export const normDeg = (d: number): number => { const r = ((d % 360) + 360) % 360; return r === 360 ? 0 : tidy(r) }
/** A clock heading (degrees clockwise from 12) as the SVG unit vector (sin h, −cos h). */
export const clockVec = (h: number): Pt => ({ x: tidy(Math.sin(rad(h))), y: tidy(-Math.cos(rad(h))) })
/** The clock heading of `p` seen from `c`, in [0, 360). */
export const clockOf = (p: Pt, c: Pt): number => normDeg(deg(Math.atan2(p.x - c.x, c.y - p.y)))
const add = (a: Pt, b: Pt): Pt => ({ x: a.x + b.x, y: a.y + b.y })
const sub = (a: Pt, b: Pt): Pt => ({ x: a.x - b.x, y: a.y - b.y })
const mul = (a: Pt, k: number): Pt => ({ x: a.x * k, y: a.y * k })
const dot = (a: Pt, b: Pt) => a.x * b.x + a.y * b.y
const unit = (v: Pt): Pt => { const l = Math.hypot(v.x, v.y); return l > 0 ? { x: v.x / l, y: v.y / l } : { x: 0, y: -1 } }
/** a quarter turn clockwise on the screen: up becomes right */
const perp = (v: Pt): Pt => ({ x: -v.y, y: v.x })
/** a point at offset `o` across and `t` along a direction, from a centre */
const along = (c: Pt, across: Pt, o: number, d: Pt, t: number): Pt => add(c, add(mul(across, o), mul(d, t)))

// ---- paths (two decimals: short strings, nothing visible lost at these sizes) ----
const num = (v: number): string => { const r = Math.round(v * 100) / 100; return Object.is(r, -0) || r === 0 ? '0' : String(r) }
const xy = (p: Pt) => `${num(p.x)} ${num(p.y)}`
export const ringPath = (r: Ring): string => (r.length ? `M${xy(r[0]!)}${r.slice(1).map((p) => `L${xy(p)}`).join('')}Z` : '')
/** every ring of a shape, holes included: drawn and clipped with the even-odd rule */
export const shapePath = (s: Pick<LookShape, 'outers' | 'holes'>): string => [...s.outers, ...s.holes].map(ringPath).join('')
const polyline = (pts: readonly Pt[]): string => `M${pts.map(xy).join('L')}`
const circlePath = (c: Pt, r: number): string => `M${num(c.x - r)} ${num(c.y)}a${num(r)} ${num(r)} 0 1 0 ${num(2 * r)} 0a${num(r)} ${num(r)} 0 1 0 ${num(-2 * r)} 0`

// ---- the generic shapes ----
/** The generic shape for the areas of a job (or the types a task applies to): a green round, a tee a block, a fairway, an approach, a surround or a
 *  rough a strip; the most frequent wins (ties: round, block, strip); round when no code says. A picture only: nothing decides on it (rule 5). */
const FRAME_OF_AREA_TYPE: Readonly<Record<string, LookFrame>> = { green: 'round', tee: 'block', fairway: 'strip', approach: 'strip', surround: 'strip', rough: 'strip' }
export function frameFor(areaTypeCodes: readonly string[]): LookFrame {
  const votes: Record<LookFrame, number> = { round: 0, block: 0, strip: 0 }
  for (const code of areaTypeCodes) if (Object.hasOwn(FRAME_OF_AREA_TYPE, code)) votes[FRAME_OF_AREA_TYPE[code]!]++
  return LOOK_FRAMES.reduce<LookFrame>((best, f) => (votes[f] > votes[best] ? f : best), 'round')
}
/** an ellipse, clockwise on the screen from 12 */
const ellipse = (c: Pt, rx: number, ry: number, n = 64): Ring => Array.from({ length: n }, (_, i) => { const v = clockVec((360 * i) / n); return { x: c.x + rx * v.x, y: c.y + ry * v.y } })
/** a rectangle with rounded corners, clockwise on the screen */
function roundedRect(c: Pt, w: number, h: number, r: number, k = 8): Ring {
  const hx = w / 2 - r, hy = h / 2 - r
  const corners: Array<[Pt, number]> = [[{ x: c.x + hx, y: c.y - hy }, 0], [{ x: c.x + hx, y: c.y + hy }, 90], [{ x: c.x - hx, y: c.y + hy }, 180], [{ x: c.x - hx, y: c.y - hy }, 270]]
  return corners.flatMap(([cc, from]) => Array.from({ length: k + 1 }, (_, i) => add(cc, mul(clockVec(from + (90 * i) / k), r))))
}
export function genericShape(frame: LookFrame, grid: LookGrid): LookShape {
  const g = grid, c = { x: g / 2, y: g / 2 }
  const outer = frame === 'round' ? ellipse(c, 0.4 * g, 0.43 * g) : frame === 'block' ? roundedRect(c, 0.64 * g, 0.78 * g, 0.1 * g) : roundedRect(c, 0.42 * g, 0.9 * g, 0.18 * g)
  return { outers: [outer], holes: [], c, box: g }
}
/** A ring scaled about a centre. */
export const scaleRing = (r: Ring, c: Pt, k: number): Ring => r.map((p) => add(c, mul(sub(p, c), k)))
const ringArea = (r: Ring): number => { let a = 0; for (let i = 0; i < r.length; i++) { const p = r[i]!, q = r[(i + 1) % r.length]!; a += p.x * q.y - q.x * p.y } return a / 2 }
const largest = (rings: readonly Ring[]): Ring | undefined => [...rings].sort((a, b) => Math.abs(ringArea(b)) - Math.abs(ringArea(a)))[0]

// ---- measures on a shape ----
/** How far the shape reaches either side of its centre along a direction (the outer rings' points projected on it). */
export function extentAlong(rings: readonly Ring[], c: Pt, v: Pt): [number, number] {
  let lo = Infinity, hi = -Infinity
  for (const r of rings) for (const p of r) { const t = dot(sub(p, c), v); if (t < lo) lo = t; if (t > hi) hi = t }
  return lo <= hi ? [lo, hi] : [0, 0]
}
export interface Band { readonly index: number; readonly lo: number; readonly hi: number; readonly points: Ring }
/** `n` bands along an axis (a clock angle), side by side across it, together spanning exactly the shape; each band runs far past the shape on both
 *  ends: the picture clips them to it (an SVG clipPath, never a computed intersection). */
export function bandsAt(shape: LookShape, angle: number, n = 7): Band[] {
  const d = clockVec(angle), across = clockVec(angle + 90)
  const [lo, hi] = extentAlong(shape.outers, shape.c, across)
  const w = (hi - lo) / n, L = shape.box * 2
  return Array.from({ length: n }, (_, i) => {
    const o1 = lo + i * w, o2 = o1 + w
    return { index: i, lo: o1, hi: o2, points: [along(shape.c, across, o1, d, -L), along(shape.c, across, o2, d, -L), along(shape.c, across, o2, d, L), along(shape.c, across, o1, d, L)] }
  })
}
/** Where the line p0 + t·d crosses the rings: the longest stretch inside, as [t1, t2]; null when the line misses the shape. */
export function chord(rings: readonly Ring[], p0: Pt, d: Pt): [number, number] | null {
  const ts: number[] = []
  for (const r of rings) for (let i = 0; i < r.length; i++) {
    const a = r[i]!, e = sub(r[(i + 1) % r.length]!, a), cross = d.x * e.y - d.y * e.x
    if (Math.abs(cross) < 1e-12) continue
    const w = sub(a, p0), t = (w.x * e.y - w.y * e.x) / cross, s = (w.x * d.y - w.y * d.x) / cross
    if (s >= 0 && s < 1) ts.push(t)
  }
  ts.sort((x, y) => x - y)
  let best: [number, number] | null = null
  for (let i = 0; i + 1 < ts.length; i += 2) if (!best || ts[i + 1]! - ts[i]! > best[1] - best[0]) best = [ts[i]!, ts[i + 1]!]
  return best && best[1] - best[0] > 1e-9 ? best : null
}
/** Where a ray from `c` at clock heading `h` leaves a ring (the farthest crossing), and the ring's tangent there turned clockwise on the screen. */
export function ringExit(ring: Ring, c: Pt, h: number): { p: Pt; cw: Pt } | null {
  const d = clockVec(h)
  let best: { t: number; e: Pt } | null = null
  for (let i = 0; i < ring.length; i++) {
    const a = ring[i]!, e = sub(ring[(i + 1) % ring.length]!, a), cross = d.x * e.y - d.y * e.x
    if (Math.abs(cross) < 1e-12) continue
    const w = sub(a, c), t = (w.x * e.y - w.y * e.x) / cross, s = (w.x * d.y - w.y * d.x) / cross
    if (s >= 0 && s < 1 && t > 0 && (!best || t > best.t)) best = { t, e }
  }
  if (!best) return null
  const tangent = unit(best.e), circle = clockVec(h + 90)
  return { p: add(c, mul(d, best.t)), cw: dot(tangent, circle) >= 0 ? tangent : mul(tangent, -1) }
}

// ---- marks ----
export type MarkRole = 'band' | 'outline' | 'axis' | 'arrow' | 'chevron' | 'divider' | 'path' | 'loop' | 'slash' | 'ground' | 'roller' | 'reel' | 'blade' | 'arm'
/** One stroke or fill of a picture, in currentColor. `fill`: filled at that opacity (else stroked with `width`); `clip`: clipped to the shape (the
 *  bands); `evenodd`: the even-odd rule (a shape with holes). `at` / `toward` / `from` / `to` / `heads` / `r` say what the mark is, for the tests (and the
 *  dial); the SVG reads only `d` and the paint. */
export interface Mark {
  readonly role: MarkRole
  readonly d: string
  readonly fill?: number
  readonly width?: number
  readonly opacity?: number
  readonly dash?: string
  readonly clip?: boolean
  readonly evenodd?: boolean
  readonly at?: Pt
  readonly toward?: number
  readonly from?: Pt
  readonly to?: Pt
  readonly heads?: 1 | 2
  readonly r?: number
}
export interface LookDrawing { readonly code: SettingLookCode; readonly clip: string; readonly marks: readonly Mark[] }

const WIDTHS: Record<LookGrid, { outline: number; line: number; thin: number; head: number; chevron: number }> = {
  24: { outline: 1.3, line: 1.7, thin: 1.1, head: 2.4, chevron: 4.6 },
  48: { outline: 1.9, line: 2.5, thin: 1.5, head: 4.2, chevron: 8.6 },
}
/** the two arms of an arrowhead at `tip`, pointing along the unit `u` */
const headPath = (tip: Pt, u: Pt, size: number): string => { const back = sub(tip, mul(u, size)), side = mul(perp(u), size * 0.72); return `M${xy(add(back, side))}L${xy(tip)}L${xy(sub(back, side))}` }
function arrowMark(role: 'axis' | 'arrow', from: Pt, to: Pt, head: number, heads: 1 | 2, width: number): Mark {
  const u = unit(sub(to, from)), h = Math.min(head, Math.hypot(to.x - from.x, to.y - from.y) / 2.2)
  const d = `M${xy(from)}L${xy(to)}${headPath(to, u, h)}${heads === 2 ? headPath(from, mul(u, -1), h) : ''}`
  return { role, d, width, from, to, at: to, toward: clockOf(to, from), heads }
}
function chevronMark(at: Pt, toward: number, size: number, width: number): Mark {
  const u = clockVec(toward), tip = add(at, mul(u, size * 0.4)), back = sub(at, mul(u, size * 0.4)), side = mul(perp(u), size * 0.55)
  return { role: 'chevron', d: `M${xy(add(back, side))}L${xy(tip)}L${xy(sub(back, side))}`, width, at, toward: normDeg(toward) }
}
/** the line along an axis at an offset across it, inside the shape, with a margin at each end */
function lineAcross(shape: LookShape, angle: number, offset: number, margin = 0.16): { a: Pt; b: Pt; mid: Pt } | null {
  const d = clockVec(angle), p0 = add(shape.c, mul(clockVec(angle + 90), offset))
  const ch = chord(shape.outers, p0, d)
  if (!ch) return null
  const m = margin * (ch[1] - ch[0])
  return { a: add(p0, mul(d, ch[0] + m)), b: add(p0, mul(d, ch[1] - m)), mid: add(p0, mul(d, (ch[0] + ch[1]) / 2)) }
}

/** The marks of one picture on one shape; null for a code the library does not know (a code of a later build, a word). */
export function drawLook(code: string, shape: LookShape, grid: LookGrid): LookDrawing | null {
  const look = lookOf(code)
  if (!look) return null
  const W = WIDTHS[grid]
  const clip = shapePath(shape)
  const outline: Mark = { role: 'outline', d: clip, width: W.outline, evenodd: true }
  const bandFills = (angle: number, fill: number): Mark[] => bandsAt(shape, angle).filter((b) => b.index % 2 === 0).map((b) => ({ role: 'band', d: ringPath(b.points), fill, clip: true }))
  const marks: Mark[] = []
  const done = (): LookDrawing => ({ code: code as SettingLookCode, clip, marks })

  if (look.family === 'dir') {
    marks.push(...bandFills(look.axis, 0.16), outline)
    const across = clockVec(look.axis + 90)
    const [lo, hi] = extentAlong(shape.outers, shape.c, across)
    const line = lineAcross(shape, look.axis, (lo + hi) / 2)
    if (line) marks.push(look.heading === null ? arrowMark('axis', line.a, line.b, W.head, 2, W.line) : chevronMark(line.mid, look.heading, W.chevron, W.line * 1.15))
    return done()
  }
  if (look.family === 'pat') {
    const up = clockVec(0), right = clockVec(90)
    const [lo, hi] = extentAlong(shape.outers, shape.c, right)
    if (look.pattern === 'stripes') {
      const angle = look.angle ?? 0
      marks.push(...bandFills(angle, 0.16), outline)
      if (angle === 0) {
        // along the line of play: an arrow on each clear band, toward 12 (or toward 6 for the reverse cut); on bands too narrow for three heads (a strip
        // at pictogram size), on the middle one only
        const bands = bandsAt(shape, 0), w = (hi - lo) / 7, which = w >= W.head * 0.66 ? [1, 3, 5] : [3]
        for (const b of bands.filter((x) => which.includes(x.index))) {
          const l = lineAcross(shape, 0, (b.lo + b.hi) / 2, 0.14)
          if (l) marks.push(look.reverse ? arrowMark('arrow', l.b, l.a, Math.min(W.head, w * 0.9), 1, W.thin) : arrowMark('arrow', l.a, l.b, Math.min(W.head, w * 0.9), 1, W.thin))
        }
      }
      return done()
    }
    if (look.pattern === 'checker' || look.pattern === 'diamond') {
      const [a1, a2] = look.pattern === 'checker' ? [0, 90] : [45, 135]
      marks.push(...bandFills(a1, 0.12), ...bandFills(a2, 0.12), outline)
      return done()
    }
    if (look.pattern === 'half') {
      // 50/50: the shape split along the line of play, the left half toward 12, the right half toward 6
      const m = (lo + hi) / 2, L = shape.box * 2
      marks.push({ role: 'band', d: ringPath([along(shape.c, right, lo, up, -L), along(shape.c, right, m, up, -L), along(shape.c, right, m, up, L), along(shape.c, right, lo, up, L)]), fill: 0.16, clip: true }, outline)
      const div = lineAcross(shape, 0, m, 0.04)
      if (div) marks.push({ role: 'divider', d: polyline([div.a, div.b]), width: W.thin, opacity: 0.7 })
      const left = lineAcross(shape, 0, (lo + m) / 2, 0.18), rightHalf = lineAcross(shape, 0, (m + hi) / 2, 0.18)
      const head = Math.min(W.head, ((hi - lo) / 4) * 0.8)
      if (left) marks.push(arrowMark('arrow', left.a, left.b, head, 1, W.line))
      if (rightHalf) marks.push(arrowMark('arrow', rightHalf.b, rightHalf.a, head, 1, W.line))
      return done()
    }
    if (look.pattern === 'box') {
      // up-and-back: one long path, up the first pass, round, down the second, round, up the third, a chevron on each pass saying its sense; where the
      // shape leaves the passes too short for their turns, three arrows in alternate senses say the same
      const span = hi - lo, offs = [0.22, 0.5, 0.78].map((f) => lo + f * span), r = 0.14 * span
      const chords = offs.map((o) => chord(shape.outers, add(shape.c, mul(right, o)), up))
      marks.push(outline)
      if (!chords.every((c) => c)) return done()
      const inset = 0.08 * shape.box
      const top = Math.min(...chords.map((c) => c![1])) - inset - r, bottom = Math.max(...chords.map((c) => c![0])) + inset + r
      const P = (o: number, t: number) => along(shape.c, right, o, up, t)
      if (top - bottom >= 2.2 * r) {
        // sweep 1 turns clockwise on the screen: over the top from the first pass to the second; sweep 0 under the bottom from the second to the third
        const d = `M${xy(P(offs[0]!, bottom))}L${xy(P(offs[0]!, top))}A${num(r)} ${num(r)} 0 0 1 ${xy(P(offs[1]!, top))}L${xy(P(offs[1]!, bottom))}A${num(r)} ${num(r)} 0 0 0 ${xy(P(offs[2]!, bottom))}L${xy(P(offs[2]!, top))}`
        marks.push({ role: 'path', d, width: W.thin })
        const midT = (top + bottom) / 2, size = Math.min(W.chevron * 0.8, 1.6 * r)
        marks.push(chevronMark(P(offs[0]!, midT), 0, size, W.thin), chevronMark(P(offs[1]!, midT), 180, size, W.thin), chevronMark(P(offs[2]!, midT), 0, size, W.thin))
      } else {
        const head = Math.min(W.head, 0.11 * span)
        offs.forEach((o, i) => { const l = lineAcross(shape, 0, o, 0.14); if (l) marks.push(i % 2 === 0 ? arrowMark('arrow', l.a, l.b, head, 1, W.thin) : arrowMark('arrow', l.b, l.a, head, 1, W.thin)) })
      }
      return done()
    }
    // around the outline: concentric loops
    marks.push(outline)
    for (const k of [0.66, 0.33]) marks.push({ role: 'loop', d: [...shape.outers].map((r) => ringPath(scaleRing(r, shape.c, k))).join(''), width: W.thin })
    return done()
  }
  if (look.family === 'lap') {
    const ring = largest(shape.outers)
    if (!ring) return done()
    const loop = scaleRing(ring, shape.c, 0.88)
    marks.push({ role: 'band', d: clip, fill: 0.12, evenodd: true })
    if (look.sense === 'none') {
      marks.push({ role: 'loop', d: ringPath(loop), width: W.line, opacity: 0.4, dash: grid === 24 ? '2 2' : '4 3.5' })
      const k = 0.3 * shape.box
      marks.push({ role: 'slash', d: polyline([add(shape.c, { x: -k, y: k }), add(shape.c, { x: k, y: -k })]), width: W.line })
      return done()
    }
    marks.push({ role: 'loop', d: ringPath(loop), width: W.line })
    if (look.sense === 'cw' || look.sense === 'ccw') {
      for (const h of [60, 240]) {
        const e = ringExit(loop, shape.c, h)
        if (!e) continue
        const dir = look.sense === 'cw' ? e.cw : mul(e.cw, -1)
        marks.push(chevronMark(e.p, clockOf(add(shape.c, dir), shape.c), W.chevron, W.line))
      }
    }
    return done()
  }
  // tool: a side view, whatever the shape — the ground, the roller behind, the reel in front, above the ground (raised) or on it (lowered)
  const g = grid, groundY = 0.84 * g
  const roller = { x: 0.75 * g, y: groundY - 0.11 * g }, rr = 0.11 * g
  const reelR = 0.2 * g, reel = { x: 0.35 * g, y: look.working ? groundY - reelR : groundY - reelR - 0.15 * g }
  marks.push({ role: 'ground', d: polyline([{ x: 0.06 * g, y: groundY }, { x: 0.94 * g, y: groundY }]), width: W.thin, at: { x: 0.5 * g, y: groundY } })
  marks.push({ role: 'arm', d: polyline([roller, reel]), width: W.line })
  marks.push({ role: 'roller', d: circlePath(roller, rr), width: W.line, at: roller, r: rr })
  marks.push({ role: 'reel', d: circlePath(reel, reelR), width: W.line, at: reel, r: reelR })
  const blades = [20, 140, 260].map((a) => polyline([add(reel, mul(clockVec(a), reelR)), add(reel, mul(clockVec(a + 120), reelR * 0.35)), add(reel, mul(clockVec(a + 200), reelR))])).join('')
  marks.push({ role: 'blade', d: blades, width: W.thin })
  if (!look.working) marks.push(chevronMark({ x: reel.x, y: reel.y - reelR - 0.11 * g }, 0, W.chevron * 0.9, W.line))
  return done()
}

// ---- the dial: the club's direction choices as spokes of a green (LookPicker) ----
/** Where a direction picture's spoke ends on the dial: a first pass at its heading; an axis at its end over the top of the green (6–12 at 12,
 *  7:30–1:30 at 1:30, 3–9 at 3, 4:30–10:30 at 10:30), so that four axes fan over the green seen from where the play arrives. Null for any other code. */
export function dialSpokeHeading(code: string): number | null {
  const l = lookOf(code)
  if (!l || l.family !== 'dir') return null
  if (l.heading !== null) return normDeg(l.heading)
  return l.axis <= 90 ? l.axis : normDeg(l.axis + 180)
}
export interface DialLayout {
  readonly width: number; readonly height: number; readonly c: Pt; readonly radius: number; readonly green: { readonly rx: number; readonly ry: number }
  readonly buttonW: number; readonly buttonH: number
  /** top-left corner of each button, in px, in the order of the headings */
  readonly buttons: ReadonlyArray<{ readonly left: number; readonly top: number }>
}
/** The dial's size and its buttons' places in CSS px: each button centred on its spoke's end at `radius` from the centre, the box just holding the
 *  buttons and the green. 84 × 56 buttons at 125 px never overlap, even at every 45°, and the widest dial is 334 px (a 375-px phone has 343). */
export function dialLayout(headings: readonly number[], opts: { radius?: number; buttonW?: number; buttonH?: number; green?: number } = {}): DialLayout {
  const radius = opts.radius ?? 125, buttonW = opts.buttonW ?? 84, buttonH = opts.buttonH ?? 56, gr = opts.green ?? 52
  const green = { rx: gr, ry: gr * 1.07 }
  const ends = headings.map((h) => mul(clockVec(h), radius))
  const xs = [...ends.flatMap((e) => [e.x - buttonW / 2, e.x + buttonW / 2]), -green.rx - 6, green.rx + 6]
  const ys = [...ends.flatMap((e) => [e.y - buttonH / 2, e.y + buttonH / 2]), -green.ry - 6, green.ry + 6]
  const minX = Math.min(...xs), minY = Math.min(...ys)
  const c = { x: -minX, y: -minY }
  return {
    width: Math.max(...xs) - minX, height: Math.max(...ys) - minY, c, radius, green, buttonW, buttonH,
    buttons: ends.map((e) => ({ left: c.x + e.x - buttonW / 2, top: c.y + e.y - buttonH / 2 })),
  }
}
